import { ChangeEvent, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { WarningFilled } from '@mint-lib/icons';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  typographyVariants,
} from '@mint-lib/tw-components';
import type { WhitelistedEmails } from '@mint-sdk/api-data-ingestion';
import { z } from 'zod';

import {
  useCreateWhitelistedEmail,
  useUpdateWhitelistedEmail,
} from '../../queries/index.js';
import { Input } from '../atoms/Input.jsx';

const emailSchema = z.email();

type WhitelistedEmailDialogProps = {
  email?: WhitelistedEmails.WhitelistedEmail;
  existingEmails: WhitelistedEmails.WhitelistedEmail[];
  onClose: () => void;
};

export const WhitelistedEmailDialog = ({
  email: existingEmail,
  existingEmails,
  onClose,
}: WhitelistedEmailDialogProps) => {
  const { t } = useTranslation('@myn/import-hub');

  const isEditMode = !!existingEmail;

  const [emailValue, setEmailValue] = useState(existingEmail?.email ?? '');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutateAsync: createEmail } = useCreateWhitelistedEmail();
  const { mutateAsync: updateEmail } = useUpdateWhitelistedEmail();

  const trimmedEmail = emailValue.trim();

  const isDuplicate = (value: string) => {
    const trimmed = value.trim().toLowerCase();
    return existingEmails.some(
      (item) =>
        item.email.toLowerCase() === trimmed && item.id !== existingEmail?.id,
    );
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmailValue(value);

    if (isDuplicate(value)) {
      setErrorMessage(
        t('This email already exists. Choose a unique email to proceed.'),
      );
    } else {
      setErrorMessage('');
    }
  };

  const handleSubmit = async () => {
    if (!emailSchema.safeParse(trimmedEmail).success) {
      setErrorMessage(t('Please enter a valid email address.'));
      return;
    }

    setIsSubmitting(true);
    try {
      if (isEditMode) {
        await updateEmail({
          id: existingEmail.id,
          payload: { email: trimmedEmail },
        });
      } else {
        await createEmail({ email: trimmedEmail });
      }
      onClose();
    } catch (error) {
      if (
        error !== null &&
        typeof error === 'object' &&
        'type' in error &&
        error.type === 'EmailAlreadyExists'
      ) {
        setErrorMessage(
          t('This email already exists. Choose a unique email to proceed.'),
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onClose();
    }
  };

  const isUnchanged = isEditMode && trimmedEmail === existingEmail.email;
  const isFormValid = trimmedEmail.length > 0 && !errorMessage && !isUnchanged;

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogRoot className="w-[520px]">
          <DialogHeader>
            <DialogTitle>
              {isEditMode ? t('Edit trusted email') : t('Add trusted email')}
            </DialogTitle>
          </DialogHeader>
          <DialogContent className="flex flex-col gap-4">
            <DialogDescription>
              {t(
                'Add an external email address that you want to be marked as safe for auto\u2011import.',
              )}
            </DialogDescription>
            <div>
              <Input
                placeholder={t('Add email')}
                value={emailValue}
                onChange={handleEmailChange}
                color={errorMessage ? 'error' : undefined}
                autoComplete="off"
              />
              {errorMessage && (
                <div className="inline-flex items-center gap-1 pt-1">
                  <WarningFilled className="size-4 fill-error-main" />
                  <p
                    className={typographyVariants({
                      variant: 'helperText01',
                      className: 'text-error-main',
                    })}
                  >
                    {errorMessage}
                  </p>
                </div>
              )}
            </div>
          </DialogContent>
          <DialogFooter>
            <Button size="large" variant="text" onClick={onClose}>
              {t('Cancel')}
            </Button>
            <Button
              size="large"
              onClick={handleSubmit}
              disabled={!isFormValid || isSubmitting}
            >
              {t('Save')}
            </Button>
          </DialogFooter>
        </DialogRoot>
      </DialogPortal>
    </Dialog>
  );
};
